import { useFormik } from 'formik'
import * as Yup from 'yup'
import styles from './PatientDataForm.module.css'
import { useAddPatientMutation } from '../../redux/api/patientApi'
import { useState } from 'react'
import { useSelector } from 'react-redux'

const validationSchema = Yup.object({
  uid: Yup.string().required('UID is required'),
  age: Yup.number()
    .required('Age is required')
    .min(0, 'Age must be positive'),
  standard_test: Yup.object({
    name: Yup.string().required('Test name is required'),
    score: Yup.number()
      .required('Score is required')
      .min(0, 'Score must be positive'),
    diagnosed_conditions: Yup.string(), // <-- string, not array
    date: Yup.string().required('Date is required'),
  }),
  nema_test: Yup.object({
    name: Yup.string().required('NEMA test name is required'),
    score: Yup.number()
      .required('Score is required')
      .min(0, 'Score must be positive'),
    diagnosed_conditions: Yup.string(), // <-- string, not array
    scan_date: Yup.string().required('Scan date is required'),
  }),
  therapies_taken: Yup.string(),
  past_data: Yup.string(),
})

const initialValues = {
  uid: '',
  age: '',
  standard_test: {
    name: '',
    score: '',
    diagnosed_conditions: '', // <-- string
    date: '',
  },
  nema_test: {
    name: '',
    score: '',
    diagnosed_conditions: '', // <-- string
    scan_date: '',
  },
  therapies_taken: '',
  past_data: '',
}

const PatientDataForm = () => {
  const [addPatient, { isLoading }] = useAddPatientMutation()
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const { selectedUser } = useSelector((state) => state.user)

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setError('')
    setSuccess('')
    try {
      setSubmitting(true)
      const payload = {
        ...values,
        uid: selectedUser?.id || '',
        standard_test: {
          ...values.standard_test,
          diagnosed_conditions: values.standard_test.diagnosed_conditions
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        },
        nema_test: {
          ...values.nema_test,
          diagnosed_conditions: values.nema_test.diagnosed_conditions
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        },
      }
      await addPatient(payload)
      setSuccess('Patient data saved successfully!')
      resetForm()
    } catch (err) {
      setError(err?.data?.error || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      uid: selectedUser?.label || '',
      age: selectedUser?.age || '',
    },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  return (
    <form className={styles.form} onSubmit={formik.handleSubmit}>
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label htmlFor='uid' className={styles.label}>
            UID
          </label>
          <input
            id='uid'
            name='uid'
            type='text'
            placeholder='Enter UID'
            value={formik.values.uid}
            readOnly
          />
          {formik.touched.uid && formik.errors.uid && (
            <div className={styles.error}>{formik.errors.uid}</div>
          )}
        </div>
        <div className={styles.field}>
          <label htmlFor='age' className={styles.label}>
            Age
          </label>
          <input
            id='age'
            name='age'
            type='number'
            placeholder='Enter age'
            value={formik.values.age}
            readOnly
          />
          {formik.touched.age && formik.errors.age && (
            <div className={styles.error}>{formik.errors.age}</div>
          )}
        </div>
        <fieldset className={styles.fieldset}>
          <legend>Standard Test</legend>
          <div className={styles.field}>
            <label htmlFor='standard_test.name' className={styles.label}>
              Name
            </label>
            <input
              id='standard_test.name'
              name='standard_test.name'
              type='text'
              placeholder='Test name'
              onChange={formik.handleChange}
              value={formik.values.standard_test.name}
            />
            {formik.touched.standard_test?.name &&
              formik.errors.standard_test?.name && (
                <div className={styles.error}>
                  {formik.errors.standard_test.name}
                </div>
              )}
          </div>
          <div className={styles.field}>
            <label htmlFor='standard_test.score' className={styles.label}>
              Score
            </label>
            <input
              id='standard_test.score'
              name='standard_test.score'
              type='number'
              placeholder='Test score'
              onChange={formik.handleChange}
              value={formik.values.standard_test.score}
            />
            {formik.touched.standard_test?.score &&
              formik.errors.standard_test?.score && (
                <div className={styles.error}>
                  {formik.errors.standard_test.score}
                </div>
              )}
          </div>
          <div className={styles.field}>
            <label
              htmlFor='standard_test.diagnosed_conditions'
              className={styles.label}
            >
              Diagnosed Conditions (comma separated)
            </label>
            <input
              id='standard_test.diagnosed_conditions'
              name='standard_test.diagnosed_conditions'
              type='text'
              placeholder='e.g. Mild Cognitive Impairment'
              onChange={formik.handleChange}
              value={formik.values.standard_test.diagnosed_conditions}
            />
            {formik.touched.standard_test?.diagnosed_conditions &&
              formik.errors.standard_test?.diagnosed_conditions && (
                <div className={styles.error}>
                  {formik.errors.standard_test.diagnosed_conditions}
                </div>
              )}
          </div>
          <div className={styles.field}>
            <label htmlFor='standard_test.date' className={styles.label}>
              Date
            </label>
            <input
              id='standard_test.date'
              name='standard_test.date'
              type='date'
              onChange={formik.handleChange}
              value={formik.values.standard_test.date}
            />
            {formik.touched.standard_test?.date &&
              formik.errors.standard_test?.date && (
                <div className={styles.error}>
                  {formik.errors.standard_test.date}
                </div>
              )}
          </div>
        </fieldset>
        <fieldset className={styles.fieldset}>
          <legend>NEMA</legend>
          <div className={styles.field}>
            <label htmlFor='nema_test.name' className={styles.label}>
              Name
            </label>
            <input
              id='nema_test.name'
              name='nema_test.name'
              type='text'
              placeholder='NEMA test name'
              onChange={formik.handleChange}
              value={formik.values.nema_test.name}
            />
            {formik.touched.nema_test?.name &&
              formik.errors.nema_test?.name && (
                <div className={styles.error}>
                  {formik.errors.nema_test.name}
                </div>
              )}
          </div>
          <div className={styles.field}>
            <label htmlFor='nema_test.score' className={styles.label}>
              Score
            </label>
            <input
              id='nema_test.score'
              name='nema_test.score'
              type='number'
              placeholder='NEMA score'
              onChange={formik.handleChange}
              value={formik.values.nema_test.score}
            />
            {formik.touched.nema_test?.score &&
              formik.errors.nema_test?.score && (
                <div className={styles.error}>
                  {formik.errors.nema_test.score}
                </div>
              )}
          </div>
          <div className={styles.field}>
            <label
              htmlFor='nema_test.diagnosed_conditions'
              className={styles.label}
            >
              Diagnosed Conditions (comma separated)
            </label>
            <input
              id='nema_test.diagnosed_conditions'
              name='nema_test.diagnosed_conditions'
              type='text'
              placeholder='e.g. ADHD, Mild Anxiety'
              onChange={formik.handleChange}
              value={formik.values.nema_test.diagnosed_conditions}
            />
            {formik.touched.nema_test?.diagnosed_conditions &&
              formik.errors.nema_test?.diagnosed_conditions && (
                <div className={styles.error}>
                  {formik.errors.nema_test.diagnosed_conditions}
                </div>
              )}
          </div>
          <div className={styles.field}>
            <label htmlFor='nema_test.scan_date' className={styles.label}>
              Scan Date
            </label>
            <input
              id='nema_test.scan_date'
              name='nema_test.scan_date'
              type='date'
              onChange={formik.handleChange}
              value={formik.values.nema_test.scan_date}
            />
            {formik.touched.nema_test?.scan_date &&
              formik.errors.nema_test?.scan_date && (
                <div className={styles.error}>
                  {formik.errors.nema_test.scan_date}
                </div>
              )}
          </div>
        </fieldset>
        <div className={styles.field}>
          <label htmlFor='therapies_taken' className={styles.label}>
            Therapies Taken
          </label>
          <textarea
            id='therapies_taken'
            name='therapies_taken'
            placeholder='Therapies taken (optional)'
            className={styles.textarea}
            onChange={formik.handleChange}
            value={formik.values.therapies_taken}
            rows={3}
          />
          {formik.touched.therapies_taken && formik.errors.therapies_taken && (
            <div className={styles.error}>{formik.errors.therapies_taken}</div>
          )}
        </div>
        <div className={styles.field}>
          <label htmlFor='past_data' className={styles.label}>
            Past Data
          </label>
          <textarea
            id='past_data'
            name='past_data'
            placeholder='Past data (optional)'
            className={styles.textarea}
            onChange={formik.handleChange}
            value={formik.values.past_data}
            rows={3}
          />
          {formik.touched.past_data && formik.errors.past_data && (
            <div className={styles.error}>{formik.errors.past_data}</div>
          )}
        </div>
      </div>
      <button
        className={styles.submit}
        type='submit'
        disabled={formik.isSubmitting || isLoading}
      >
        Submit
      </button>
      {(error || success) && (
        <div style={{ marginTop: 16 }}>
          {error && <div style={{ color: 'red' }}>{error}</div>}
          {success && <div style={{ color: 'green' }}>{success}</div>}
        </div>
      )}
    </form>
  )
}

export default PatientDataForm
